import React, { useState } from 'react';
import { 
  Store, 
  Search, 
  Tag, 
  Award, 
  ChevronRight, 
  MapPin, 
  ArrowUpRight, 
  Gift,
  ShieldCheck,
  TrendingUp,
  Percent,
  Coins
} from 'lucide-react';
import { 
  CashbackCompany, 
  CashbackCampaign,
  CashbackBannerConfig 
} from './CashbackTypes';

interface CashbackDirectoryViewProps {
  companies: CashbackCompany[];
  campaigns: CashbackCampaign[];
  bannerConfig?: CashbackBannerConfig;
  onSelectCompanyToSubmit: (companyId: string) => void;
  onOpenWallet: () => void;
}

export const CashbackDirectoryView: React.FC<CashbackDirectoryViewProps> = ({
  companies,
  campaigns,
  bannerConfig,
  onSelectCompanyToSubmit,
  onOpenWallet
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(companies.map(c => c.category)))];

  const filteredCompanies = companies.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = selectedCategory === 'all' || c.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  // Top Companies Ranking
  const topCompanies = [...companies].sort((a, b) => b.settings.default_percent - a.settings.default_percent);

  return (
    <div className="w-full space-y-8">
      {/* HERO BANNER SECTION WITH HAPPY PEOPLE & REAL STORES PHOTO */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-500/20 via-emerald-600/20 to-slate-950 text-white shadow-lg border border-emerald-500/30 group">
        {/* Background Image with optimized visibility and soft hover zoom */}
        <img 
          src={bannerConfig?.banner_image_url || '/images/happy_cashback_banner.jpg'} 
          alt="Pessoas Felizes Comprando com Cashback" 
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover object-center md:object-right opacity-90 group-hover:scale-105 transition-transform duration-700 pointer-events-none"
        />
        
        {/* Ambient Blur Lights */}
        <div className="absolute top-0 right-1/4 w-72 h-72 bg-amber-400/30 rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute bottom-0 right-10 w-80 h-80 bg-emerald-500/30 rounded-full blur-[100px] pointer-events-none" />

        {/* Soft Left Side Gradient & Frosted Blur for Readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-transparent pointer-events-none"></div>

        <div className="relative z-10 p-6 sm:p-10 md:p-12 max-w-2xl space-y-4">
          <div className="backdrop-blur-md bg-slate-950/50 p-6 sm:p-8 rounded-xl border border-white/10 shadow-xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/25 text-emerald-300 border border-emerald-400/40 text-xs font-black uppercase tracking-widest rounded-md backdrop-blur-md shadow-sm">
              <Coins size={14} className="animate-pulse text-amber-300" /> {bannerConfig?.badge_text || 'Clube de Cashback Multi-Empresas'}
            </span>

            <h1 className="text-2xl sm:text-3xl md:text-5xl font-black tracking-tight leading-tight text-white drop-shadow-md">
              {bannerConfig?.title || 'Receba Dinheiro de Volta em Cada Compra'}
            </h1>

            <p className="text-xs sm:text-sm md:text-base text-slate-200 font-medium leading-relaxed drop-shadow">
              {bannerConfig?.subtitle || 'Envie a nota fiscal das suas compras nos estabelecimentos parceiros e acumule saldo real na sua carteira digital para resgatar onde quiser!'}
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <button
                onClick={onOpenWallet}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-lg shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-2"
              >
                <Gift size={18} /> {bannerConfig?.cta_button_text || 'Abrir Minha Carteira & Enviar Nota'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH AND CATEGORIES BAR */}
      <div className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Pesquisar loja ou produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-600"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold uppercase transition-all shrink-0 ${
                  selectedCategory === cat
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat === 'all' ? 'Todas as Categorias' : cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURED CAMPAIGNS */}
      <div className="space-y-4">
        <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
          <Tag className="text-indigo-600" size={20} /> Campanhas de Cashback Turbinado
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {campaigns.map(camp => (
            <div key={camp.id} className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-xl p-6 text-white shadow-md space-y-3 relative overflow-hidden">
              <span className="text-[10px] font-black uppercase text-amber-300 bg-white/10 px-2 py-0.5 rounded-md">
                {camp.business_name}
              </span>
              <h4 className="text-lg font-black leading-snug">{camp.title}</h4>
              <p className="text-xs text-indigo-200">{camp.description}</p>

              <div className="pt-2 flex items-center justify-between border-t border-white/10">
                <span className="text-xl font-black text-emerald-400">
                  {camp.cashback_percent}% Cashback
                </span>
                <button
                  onClick={() => onSelectCompanyToSubmit(camp.business_id)}
                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase rounded-lg transition-colors cursor-pointer"
                >
                  Enviar Nota
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PARTICIPATING STORES GRID */}
      <div className="space-y-4">
        <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
          <Store className="text-indigo-600" size={20} /> Estabelecimentos Parceiros ({filteredCompanies.length})
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map(company => (
            <div
              key={company.id}
              className="bg-white rounded-xl overflow-hidden border border-slate-200/80 shadow-sm hover:shadow-md transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="relative h-36 bg-slate-100 overflow-hidden">
                  <img
                    src={company.banner_url || 'https://images.unsplash.com/photo-1517433670267-08bbd4be890f?w=1000&h=400&fit=crop'}
                    alt={company.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-emerald-500 text-slate-950 font-black text-xs px-2.5 py-1 rounded-md shadow-sm">
                    {company.settings.default_percent}% Cashback
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={company.logo_url}
                      alt={company.name}
                      className="w-12 h-12 rounded-lg object-cover border border-slate-200 shadow-sm shrink-0"
                    />
                    <div>
                      <h4 className="text-base font-black text-slate-800 group-hover:text-indigo-600 transition-colors">
                        {company.name}
                      </h4>
                      <span className="text-[10px] font-bold text-slate-400 uppercase">
                        {company.category} • {company.city_state}
                      </span>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-lg text-xs space-y-1 text-slate-600">
                    <div className="flex justify-between">
                      <span>Compra Mínima:</span>
                      <strong className="text-slate-800">R$ {company.settings.min_purchase.toFixed(2)}</strong>
                    </div>
                    {company.settings.first_purchase_bonus_percent && (
                      <div className="flex justify-between text-emerald-600 font-bold">
                        <span>1ª Compra:</span>
                        <span>{company.settings.first_purchase_bonus_percent}% Cashback</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0">
                <button
                  onClick={() => onSelectCompanyToSubmit(company.id)}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  Enviar Comprovante / Nota Fiscal <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
